import React from 'react';

import { Collapse, IconButton, Paper } from '@mui/material';
import ArrowDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import { useToggle } from 'react-use';
import { observer } from 'mobx-react';

import classes from './LeaderboardRowMobile.module.scss';
import { LeaderboardRowInfo } from '../../../../../interfaces/leaderboard/LeaderboardRowInfo';
import { GridTableCell } from '../../../../../../shared/components/table-components';
import { RankCell } from '../RankCell/RankCell';
import { CustomerCell } from '../CustomerCell/CustomerCell';
import {
  LEADERBOARD_COLUMN,
  LeaderboardColumn,
  RenderColumnInfo,
} from '../../../../../constants/leaderboardColumn';
import { InfoItem } from './InfoItem/InfoItem';
import { getNumberWithSignView } from '../../../../../../shared/utils/stringUtils/getNumberWithSignView/getNumberWithSignView';
import { leaderboardStore } from '../../../../../stores/leaderboardStore';
import { getValueOrDash } from '../../../../../../shared/utils/stringUtils/getValueOrDash/getValueOrDash';
import { MuscleMultiplierAdditionalInfo } from '../MuscleMultiplierAdditionalInfo/MuscleMultiplierAdditionalInfo';

type LeaderboardRowMobileProps = {
  leaderboardRow: LeaderboardRowInfo;
  leaderboardColumns: LeaderboardColumn[];
};

const LeaderboardRowMobile = observer(
  ({ leaderboardRow, leaderboardColumns }: LeaderboardRowMobileProps) => {
    const [expanded, toggleExpanded] = useToggle(false);
    const { challengeInfo } = leaderboardStore;
    const { scoringRules } = challengeInfo || {};

    const renderColumnsInfo: RenderColumnInfo = {
      [LEADERBOARD_COLUMN.RANK]: null,
      [LEADERBOARD_COLUMN.CUSTOMER]: null,
      [LEADERBOARD_COLUMN.TOTAL_SCORE]: (
        <InfoItem title="Total Score" value={getValueOrDash(leaderboardRow.totalScore)} />
      ),
      [LEADERBOARD_COLUMN.FOOD_TRACKING_PERCENT]: (
        <InfoItem
          title="Food Tracking"
          description="%"
          value={getValueOrDash(leaderboardRow.foodTrackingPercentage, '%')}
        />
      ),
      [LEADERBOARD_COLUMN.FOOD_TRACKING_RANK]: (
        <InfoItem
          title="Food Tracking"
          description="rank"
          value={getValueOrDash(leaderboardRow.foodTrackingRank)}
        />
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_PERCENT]: (
        <InfoItem
          title="Fat Loss"
          description="%"
          value={getValueOrDash(leaderboardRow.fatLossPercentage, '%')}
        />
      ),
      [LEADERBOARD_COLUMN.ADJUSTED_FAT_LOSS_PERCENT]: (
        <InfoItem
          title="Adjusted Fat Loss"
          description="%"
          value={getValueOrDash(leaderboardRow.adjustedFatLossPercentage, '%')}
        />
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_RANK]: (
        <InfoItem
          title="Fat Loss"
          description="rank"
          value={getValueOrDash(leaderboardRow.fatLossRank)}
        />
      ),
      [LEADERBOARD_COLUMN.FAT_LOSS_LBS]: (
        <InfoItem
          title="Fat Loss"
          description="lbs"
          value={getValueOrDash(leaderboardRow.fatLossLbs)}
        />
      ),
      [LEADERBOARD_COLUMN.MUSCLE_GAIN_LBS]: (
        <InfoItem
          title="Muscle Gain"
          description="lbs"
          value={
            <div>
              {getValueOrDash(getNumberWithSignView(leaderboardRow.muscleGainLbs))}
              &nbsp;
              <MuscleMultiplierAdditionalInfo
                scoringRuleType={scoringRules}
                muscleGainLbs={leaderboardRow.muscleGainLbs}
                muscleGainMultiplier={leaderboardRow.muscleGainMultiplier}
              />
            </div>
          }
        />
      ),
      [LEADERBOARD_COLUMN.MUSCLE_MULTIPLIER]: (
        <InfoItem
          title="Muscle Multiplier"
          value={
            <>{getValueOrDash(leaderboardRow.muscleGainMultiplier, '*', 'prefix')}</>
          }
        />
      ),
      [LEADERBOARD_COLUMN.ADJUSTED_SCORE_PERCENT]: (
        <InfoItem
          title="Adjusted Score"
          description="%"
          value={getValueOrDash(leaderboardRow.adjustedFatLossPercentage, '%')}
        />
      ),
    };

    return (
      <Paper className={classes.container} variant="roundedBox">
        <RankCell rank={leaderboardRow.rank} />
        <CustomerCell
          nickname={leaderboardRow.nickname}
          gender={leaderboardRow.gender}
          age={leaderboardRow.age}
        />
        <GridTableCell justify="end">
          <IconButton onClick={toggleExpanded}>
            {expanded ? (
              <ArrowUpRoundedIcon color="primary" />
            ) : (
              <ArrowDownRoundedIcon color="tertiary" />
            )}
          </IconButton>
        </GridTableCell>
        <Collapse className={classes.collapsibleContent} in={expanded} unmountOnExit>
          {leaderboardColumns.map((column) => (
            <React.Fragment key={column}>{renderColumnsInfo[column]}</React.Fragment>
          ))}
        </Collapse>
      </Paper>
    );
  },
);

export { LeaderboardRowMobile };
